// src/utils/api.js
import { getEncKey, isEncrypted, aesDecrypt } from './crypto';

// const BASE      = 'http://localhost/eoi-api/index.php';               // LOCAL
// const BASE_LIVE = 'http://localhost/eoi-api/index.php';               // LOCAL
// ── TEST (ACTIVE) ──
const BASE      = 'https://testapi.gurupunvaanii.com/index.php';      // TEST
const BASE_LIVE = 'https://testapi.gurupunvaanii.com/index.php';      // TEST
// const BASE      = 'https://api.gurupunvaanii.com/index.php';            // PRODUCTION
// const BASE_LIVE = 'https://api.gurupunvaanii.com/index.php';            // PRODUCTION

async function request(url, options) {
  let res;
  try {
    res = await fetch(url, options);
  } catch {
    return { error: 'Network error. Please check your connection.' };
  }
  const text = await res.text();
  if (!text) {
    return { error: `Empty response from server (HTTP ${res.status})` };
  }
  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch {
    const snippet = text.slice(0, 200).replace(/\s+/g, ' ');
    return { error: `Server returned invalid response (HTTP ${res.status}): ${snippet}` };
  }

  // Auto-decrypt if server returned encrypted payload
  if (isEncrypted(parsed)) {
    const key = getEncKey();
    if (!key) return { _encrypted: true };
    const result = await aesDecrypt(parsed, key);
    if (!result.ok) return { _encrypted: true, _keyError: result.error };
    try {
      return JSON.parse(result.text);
    } catch {
      return { error: 'Decrypted data is not valid JSON' };
    }
  }

  return parsed;
}

const get = (action, params = {}) => {
  const q = new URLSearchParams({ action, ...params }).toString();
  return request(`${BASE}?${q}`);
};

const post = (action, body) =>
  request(`${BASE}?action=${action}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

export const api = {
  login: (username, password) => request(`${BASE_LIVE}?action=login`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ username, password }) }),

  // ── Mobile OTP login ───────────────────────────────────
  sendLoginOtp: (mobile) => request(`${BASE_LIVE}?action=send_login_otp`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ mobile }) }),
  verifyLoginOtp: (mobile, otp) => request(`${BASE_LIVE}?action=verify_login_otp`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ mobile, otp }) }),

  dashboard: (code_name) => get('dashboard', code_name ? { code_name } : {}),

  list: (params = {}) => get('list', params),

  detail: (id) => get('detail', { id }),

  updateStatus: (id, status) => post('status', { id, status }),

  // Bulk status update from an imported file (matched by ref_number).
  statusBulk: (items) => post('status_bulk', { items }),

  // Send the WhatsApp status-update template (Gupshup) for one submission.
  sendWhatsapp: (id) => post('send_whatsapp', { id }),

  // Bulk-send the WhatsApp status update to all applicants in the given statuses.
  sendWhatsappBulk: (statuses) => post('send_whatsapp_bulk', { statuses }),

  delete: (id) => post('delete', { id }),

  // ── Projects (master data) ─────────────────────────────
  projects: () => get('projects', { all: 1 }),

  projectDetail: (id) => get('project_detail', { id }),

  projectSave: (data) => post('project_save', data),

  projectDelete: (id) => post('project_delete', { id }),

  projectToggle: (id, is_active) => post('project_toggle', { id, is_active }),

  // ── Users (admin user management) ──────────────────────
  users: () => get('users'),

  userSave: (data) => post('user_save', data),

  userDelete: (id) => post('user_delete', { id }),

  userToggle: (id, is_active) => post('user_toggle', { id, is_active }),

  // ── Roles & permissions ────────────────────────────────
  roles: () => get('roles'),

  roleSave: (data) => post('role_save', data),

  roleDelete: (id) => post('role_delete', { id }),

  // ── Settings (login appearance, admin-configurable) ────
  settings: () => get('settings_get'),

  settingsSave: (appearance) => post('settings_save', { key: 'login_appearance', value: appearance }),

  // ── Visitors ───────────────────────────────────────────
  submitVisitor: (data) => post('submit_visitor', data),

  visitorList: (params = {}) => get('visitor_list', params),

  visitorDashboard: (params = {}) => get('visitor_dashboard', params),

  visitorDetail: (id) => get('visitor_detail', { id }),

  // Salesperson saves their Sales Audit follow-up details onto a visitor.
  visitorUpdate: (data) => post('visitor_update', data),

  // Admin assigns / reassigns / unassigns a visitor to a salesperson.
  // Pass assigned_to = 0 (or '') to clear the assignment.
  visitorAssign: (id, assigned_to) => post('visitor_assign', { id, assigned_to }),

  // A salesperson accepts (claims) a lead, or an admin clears / overrides it.
  // Pass user_id = 0 to clear; force = true lets an admin steal an accepted lead.
  // actor_name (optional) = who performed an admin override, for the history log.
  visitorAccept: (id, user_id, force = false, actor_name = '') => post('visitor_accept', { id, user_id, force, actor_name }),

  // Activity log for the Sales Visitor workflow (picks, reassigns, audit
  // submissions, re-edits). Filters: from, to, event, user_id, search, page, limit.
  visitorHistory: (params = {}) => get('visitor_history', params),

  // Admin corrects the project on a visitor record (customer picked the wrong
  // one at walk-in). actor_name (optional) = who made the change, for the log.
  visitorEditProject: (id, project_id, actor_name = '') => post('visitor_edit_project', { id, project_id, actor_name }),

  // Projects assigned to a salesperson (for the Sales Audit project grid).
  myProjects: (user_id) => get('my_projects', { user_id }),

  // Active users who can work leads — powers the admin assign dropdown.
  salespeople: () => get('salespeople'),
};
